/** Round to absorb floating-point noise when bucketing comparator values. */
const KEY_DECIMALS = 9;
function bucketKey(v) {
    // Normalize -0 and fixed-precision so equal rationals bucket together.
    return (v + 0).toFixed(KEY_DECIMALS);
}
export function cloneRow(r) {
    return { ...r };
}
/**
 * Partition items into groups sharing a comparator value, groups ordered by
 * that value ("asc" or "desc"). One group ⇒ no separation.
 */
function partition(items, keyFn, dir) {
    const groups = new Map();
    for (const it of items) {
        const k = keyFn(it);
        const kk = bucketKey(k);
        const existing = groups.get(kk);
        if (existing) {
            existing.items.push(it);
        }
        else {
            groups.set(kk, { k, items: [it] });
        }
    }
    const arr = [...groups.values()];
    arr.sort((a, b) => (dir === "asc" ? a.k - b.k : b.k - a.k));
    return arr.map((g) => g.items);
}
/** Per-team W/T from a counted game, or null if the game can't be scored. */
function pairOutcome(g) {
    if (g.status === "final") {
        if (g.homeScore === undefined || g.awayScore === undefined)
            return null;
        if (g.homeScore > g.awayScore)
            return { homeWin: true, awayWin: false, tie: false };
        if (g.homeScore < g.awayScore)
            return { homeWin: false, awayWin: true, tie: false };
        return { homeWin: false, awayWin: false, tie: true };
    }
    if (g.status === "forfeit") {
        /* v8 ignore next */
        if (!g.forfeitTeamId)
            return null;
        const homeForfeited = g.forfeitTeamId === g.homeTeamId;
        return { homeWin: homeForfeited === false, awayWin: homeForfeited, tie: false };
    }
    /* v8 ignore next */
    return null;
}
function pairKey(a, b) {
    return a < b ? `${a}|${b}` : `${b}|${a}`;
}
/**
 * Head-to-head comparator over tied set S (§7.5). Returns ordered parts if it
 * separates, or a single part [S] to ABSTAIN. Abstains when any unordered pair
 * in S never met, or when the intra-set records are circular (single part).
 */
function headToHead(S, countedPool) {
    const ids = new Set(S.map((r) => r.teamId));
    const games = countedPool.filter((g) => g.homeTeamId !== undefined &&
        g.awayTeamId !== undefined &&
        ids.has(g.homeTeamId) &&
        ids.has(g.awayTeamId));
    // Every unordered pair must have at least one meeting, else abstain.
    const met = new Set();
    for (const g of games)
        met.add(pairKey(g.homeTeamId, g.awayTeamId));
    const need = (S.length * (S.length - 1)) / 2;
    if (met.size < need)
        return [S];
    const rec = new Map();
    for (const r of S)
        rec.set(r.teamId, { w: 0, t: 0, gp: 0 });
    for (const g of games) {
        const o = pairOutcome(g);
        if (!o)
            continue;
        const h = rec.get(g.homeTeamId);
        const a = rec.get(g.awayTeamId);
        h.gp++;
        a.gp++;
        if (o.tie) {
            h.t++;
            a.t++;
        }
        else if (o.homeWin) {
            h.w++;
        }
        else {
            a.w++;
        }
    }
    const pct = (r) => {
        // Every pair in S met (checked above), so every team has ≥1 H2H game.
        const x = rec.get(r.teamId);
        return (x.w + 0.5 * x.t) / x.gp;
    };
    return partition(S, pct, "desc");
}
/**
 * The recursive tiebreaker chain with the "restart" rule (§7.4): whenever a
 * comparator separates anyone out, remaining tied teams re-enter the FULL
 * chain (which re-scopes head-to-head to the smaller set). Mutates rows in a
 * still-tied terminal group to `unresolved` and records the group.
 */
function resolveTies(S, countedPool, unresolvedGroups) {
    if (S.length === 1)
        return S;
    // Rows reaching the chain are always "played" (winPct non-null), so their
    // capped averages are non-null.
    // Comparator 1: average counted runs ALLOWED, ascending (fewer is better).
    let parts = partition(S, (r) => r.avgCountedRunsAllowed, "asc");
    if (parts.length > 1) {
        return parts.flatMap((p) => resolveTies(p, countedPool, unresolvedGroups));
    }
    // Comparator 2: average counted runs SCORED, descending (more is better).
    parts = partition(S, (r) => r.avgCountedRunsScored, "desc");
    if (parts.length > 1) {
        return parts.flatMap((p) => resolveTies(p, countedPool, unresolvedGroups));
    }
    // Comparator 3: head-to-head (may abstain).
    const h2h = headToHead(S, countedPool);
    if (h2h.length > 1) {
        return h2h.flatMap((p) => resolveTies(p, countedPool, unresolvedGroups));
    }
    // Chain exhausted → unresolved; display alphabetically, flag, and record.
    const ordered = [...S].sort((a, b) => a.teamName.localeCompare(b.teamName));
    for (const r of ordered) {
        r.unresolved = true;
        r.tiebreakNote = "unresolved — order manually at finalize";
    }
    unresolvedGroups.push(ordered.map((r) => r.teamId));
    return ordered;
}
/**
 * Order a set of rows by winPct desc, then the tiebreaker chain within each
 * winPct group. GP=0 rows (winPct null) sort below all played teams,
 * alphabetically, and are NOT flagged unresolved (they simply haven't played).
 * Returns the ordered rows and any terminal unresolved groups (each ≥2 ids).
 */
export function orderTeams(rows, countedPool) {
    const unresolvedGroups = [];
    const played = rows.filter((r) => r.winPct !== null);
    const unplayed = rows
        .filter((r) => r.winPct === null)
        .sort((a, b) => a.teamName.localeCompare(b.teamName));
    const ordered = [];
    for (const group of partition(played, (r) => r.winPct, "desc")) {
        ordered.push(...resolveTies(group, countedPool, unresolvedGroups));
    }
    ordered.push(...unplayed);
    return { ordered, unresolvedGroups };
}
//# sourceMappingURL=tiebreak.js.map